import { Box, Fade, useScrollTrigger } from '@mui/material';
import regression from "regression";

//back to top link
export const ScrollTop = (props) => {
    const { children } = props;

    const trigger = useScrollTrigger({
        disableHysteresis: true,
        threshold: 1000,
        target: document.getElementById('scroll-content') || window
    });

    const handleClick = (event) => {
        const container = document.getElementById('scroll-content');

        if (container) {
            container.scrollTo({
                top: 0,
                behavior: 'smooth',
            });
        }
    };

    return (
        <Fade in={trigger}>
            <Box
                onClick={handleClick}
                role="presentation"
                sx={{ position: 'fixed', zIndex: '9', bottom: 35, right: { xs: '18px', md: '55px' } }}
            >
                {children}
            </Box>
        </Fade>
    );
}

export const yearsRange = () => {
    const currentYear = (new Date().getFullYear() - 1);
    // get 15 years before today's date
    const yearsArray = Array.from({ length: 15 }, (_, i) => (currentYear - i).toString()).reverse();

    return yearsArray;
}

// find the 'year' parameter in the query string
export const getYearRange = (url) => {
    const yearMatch = url.match(/year=([^&]*)/);

    if (yearMatch && yearMatch[1]) {
        const yearValues = yearMatch[1].split(',');
        return yearValues;
    }

    return null;
}

// slope function
export const slopeFunction = (data, setTrendStatus, info) => {
    if (data && data.datasets && data.datasets.length > 0) {
        const datasetValues = data.datasets[0]?.data || [];

        if (datasetValues.length > 1) {
            const regressionData = datasetValues.map((dataPoint, index) => {
                if (info.chart_type !== "stackedBar" && info.chart_type !== "barChart") {
                    return [index, dataPoint.y];
                } else {
                    return [index, Number(dataPoint)];
                }
            });


            // regression
            const result = regression.linear(regressionData);
            const equation = result.equation;  // [slope, intercept]


            const slope = equation[0];
            const threshold = 0.2; // threshold for detecting "good" or "bad" trends
            const smallSlopeThreshold = 0.05; // slope as "normal" (close to flat)

            if (info.trend_should === "increase") {
                if (slope > threshold) {
                    setTrendStatus("good");
                } else if (slope < -threshold) {
                    setTrendStatus("bad");
                } else if (Math.abs(slope) <= smallSlopeThreshold) {
                    setTrendStatus("normal");
                } else {
                    setTrendStatus("normal");
                }
            } else {
                if (slope < -threshold) {
                    setTrendStatus("good");
                } else if (slope > threshold) {
                    setTrendStatus("bad");
                } else if (Math.abs(slope) <= smallSlopeThreshold) {
                    setTrendStatus("normal");
                } else {
                    setTrendStatus("normal");
                }
            }
        }
    }
}

export const getLinkDetails = (source, title) => {
    switch (source) {
        case "AQUA":
            return {
                href: `https://data.apps.fao.org/aquastat/?lang=en`,
                text: "FAO. 2025. AQUASTAT - FAO's Global Information System on Water and Agriculture. In: AQUASTAT. Licence: CC-BY-4.0",
                iconText: "AQUASTAT"
            };
        case "WB_ES_OBSTACLES":
            return {
                href: `https://www.enterprisesurveys.org/en/data/exploretopics/biggest-obstacle`,
                text: "The World Bank. 2025. Enterprise Surveys: Biggest Obstacle. Licence: CC-BY-4.0",
                iconText: "WB ES"
            };
        case "GSMA_MCI":
            return {
                href: `https://www.mobileconnectivityindex.com/assets/excelData/MCI_Data_2024.xlsx`,
                text: "GSMA. 2024. GSMA Mobile Connectivity Index. Licence: https://www.gsma.com/aboutus/legal/terms-of-use/",
                iconText: "GSMA"
            };
        case "ITU":
            return {
                href: `https://datahub.itu.int/data/?i=11624&v=`,
                text: "ITU. 2024. ITU DataHub Licence: CC BY-NC-SA 3.0 IGO",
                iconText: "ITU"
            };
        case "ASPIRE":
            return {
                href: `https://www.govindicators.org/`,
                text: "The World Bank. 2025. The Atlas of Social Protection: Indicators of Resilience and Equity Public (ASPIRE). Licence: CC-BY-4.0",
                iconText: "ASPIRE"
            };
        case "WDI":
        case "WDI_AGG2":
            return {
                href: `https://databank.worldbank.org/source/world-development-indicators`,
                text: "The World Bank. 2025. World Development Indicators (WDI). Licence: CC-BY-4.0",
                iconText: "WDI"
            };
        case "WGI":
            return {
                href: `https://www.govindicators.org`,
                text: "The World Bank. 2024. Worldwide Governance Indicators (WGI). Licence: CC-BY-4.0",
                iconText: "WGI"
            };
        case "FRA":
            return {
                href: `https://fra-data.fao.org/assessments/fra/2020/`,
                text: "FAO. 2020. Global Forest Resources Assessments (FRA). In: FRA2020. Licence: CC-BY-4.0",
                iconText: "FRA"
            };
        case "UNPD":
        //case "UNPD_FULL":
        //case "UNPD_COMPLETE":
            return {
                href: `https://population.un.org/dataportal/home`,
                text: "United Nations Population Division. 2024. World Population Prospects 2024. In UNPD Data Portal. Licence: https://www.un.org/en/about-us/copyright",
                iconText: "UNPD"
            };
        case "DATA":
            return {
                href: ` https://hdr.undp.org/data-center/thematic-composite-indices/gender-inequality-index#/indicies/GII`,
                text: "UNDP. 2024. Gender Inequality Index (GII)",
                iconText: "UNPD"
            };
        case "SCP_HAT":
            return {
                href: `https://scp-hat.org/data-download/`,
                text: "SCP Hotspot Analysis. 2024. UN Environment Programme. Licence: CC BY-NC-SA 3.0 IGO",
                iconText: "SCP"
            };
        case "SOFA":
            return {
                href: `https://www.fao.org/publications/sofa/en/`,
                text: "FAO. 2024. The State of Food and Agriculture (SOFA). Licence: CC-BY-4.0",
                iconText: "SOFA"
            };
        case "FSD":
            return {
                href: `https://www.foodsystemsdashboard.org/`,
                text: `Food Systems Dashboard. 2025. ${title}. Licence: CC-BY-NC-SA 4.0`,
                //text: "Food Systems Dashboard",
                iconText: "FSD"
            };
        case "IDMC":
            return {
                href: `https://www.internal-displacement.org/database/displacement-data`,
                text: "IDMC. 2024. Internal Displacement Monitoring Centre. Licence: CC BY-NC-SA 3.0 IGO",
                iconText: "IDMC"
            };
        case "GIEWS_ASI":
            return {
                href: `https://www.fao.org/giews/earthobservation/asis/index_1.jsp?lang=en`,
                text: "FAO. GIEWS Earth Observation. 2024. License CC-BY-4.0",
                iconText: "GIEWS"
            }
        case "INFARM":
            return {
                href: `https://infarm.fao.org/`,
                text: "FAO. InFARM System. 2024. License CC-BY-4.0",
                iconText: "FAO"
            };
        case "NIFL":
        case "SDG8":
        case "UNEAP":
        case "DWAP":
        //   case "FSD_DATA":
        //     return {
        //         href: `https://hdr.undp.org/data-center/thematic-composite-indices/gender-inequality-index#/indicies/GII`,
        //         text: "UNDP. 2024. Gender Inequality Index (GII). Licence: CC BY-NC-SA 3.0 IGO",
        //         iconText: "UNDP"
        //     };
        case "ILO_TRU":
        case "ILO_SDG8":
        case "ILO_DWAP":
        case "ILO_NIFL":
        case "ILO_UNEAP":
        case "ILO_UNEMP":
            return {
                href: `https://ilostat.ilo.org/data/`,
                text: "ILO. 2025. ILOSTAT: Labour Force Statistics. Licence: https://www.ilo.org/rights-and-permissions",
                iconText: "ILOSTAT"
            };
        // case "SDG":
        case "UNESCO_OPRI":
        case "UNESCO_SDG":
            return {
                href: `https://databrowser.uis.unesco.org/browser`,
                text: "UNESCO. 2025. UNESCO Institute for Statistics (UIS). Licence: CC-BY-4.0",
                iconText: "UNESCO"
            };
        case "USDA":
            return {
                href: `https://www.ers.usda.gov/data-products/international-agricultural-productivity/`,
                text: "USDA ERS. 2024. International Agricultural Productivity. Licence: Creative Commons CCZero",
                iconText: "USDA"
            };
        case "UNICEF":
            return {
                href: `https://data.unicef.org/topic/nutrition/diets/`,
                text: "UNICEF. 2024. Diets. Licence: CC BY 3.0 IGO",
                iconText: "UNICEF"
            };
        case "UNSD":
        case "SDG":
        case "SDG_FULL":
            return {
                href: `https://unstats.un.org/sdgs/dataportal`,
                text: "UNSD. 2024. SDG Indicators Database. Licence: CC-BY-4.0",
                iconText: "UNSD"
            };
        case "RULIS":
            return {
                href: `https://www.fao.org/in-action/rural-livelihoods-dataset-rulis/en`,
                text: "FAO. 2024. RULIS - Rural Livelihoods Information System. Licence: CC-BY-4.0",
                iconText: "RULIS"
            };
        case "WB_RAI":
            return {
                href: `https://datacatalog.worldbank.org/search/dataset/0038250/Rural-Access-Index--RAI-`,
                text: "The World Bank. 2024. Rural Access Index (RAI). Licence: CC-BY-4.0",
                iconText: "WB RAI"
            };
        case "FISHSTAT":
        case "FISHSTAT_PRODUCTION":
            return {
                href: `https://www.fao.org/fishery/en/statistics/software/fishstatj`,
                text: "FAO. 2025. FishStat: Global Aquatic Processed Production Statistics Quantity (1976 - 2023). In: FishStatJ. Licence: CC-BY-4.0",
                iconText: "FISHSTAT"
            };
        case "FISHSTAT_TRADE":
            return {
                href: `https://www.fao.org/fishery/en/statistics/software/fishstatj`,
                text: "FAO. 2025. FishStat: Global Aquatic Trade - All partners aggregated 1976-2023. In: FishStatJ. Licence: CC-BY-4.0",
                iconText: "FISHSTAT"
            };
        case "FISHSTAT_PROCESSED":
            return {
                href: `https://www.fao.org/fishery/en/statistics/software/fishstatj`,
                text: "FAO. 2025. FishStat: Global Aquatic Processed Production Statistics Quantity (1976 - 2023). In: FishStatJ. Licence: CC-BY-4.0",
                iconText: "FISHSTAT"
            };
        case "WB_LPI":
            return {
                href: `https://datacatalog.worldbank.org/search/dataset/0038649/logistics-performance-index`,
                text: "The World Bank. 2023. Logistics Performance Index (LPI) Licence: CC-BY-4.0",
                iconText: "WB LPI"
            };
        case "WB_WDI":
            return {
                href: `https://databank.worldbank.org/source/world-development-indicators`,
                text: "The World Bank. 2025. World Development Indicators (WDI). Licence: CC-BY-4.0",
                iconText: "WDI"
            };
        case "FSS":
            return {
                href: `https://datalab.fao.org/datalab/dashboard/food-systems-summit/`,
                text: `FAO. 2021. Food Systems Summit. Licence: CC-BY-4.0`,
                iconText: "FOOD SYSTEM SUMMIT"
            };
        default:
            return {
                href: `https://www.fao.org/faostat/en/#data/${source}/`,
                text: `FAO. 2025. FAOSTAT: ${source}. Licence: CC-BY-4.0`,
                iconText: "FAOSTAT"
            };
    }
};

export const countDataSources = () => {
    const switchStatement = getLinkDetails.toString();
    const returnMatches = switchStatement.match(/return\s+{[^}]+}/g) || [];

    // total count including default
    const uniqueReturns = new Set(returnMatches.map(ret => ret.replace(/\s+/g, ' ').trim()));

    return uniqueReturns.size;
};

/**
 * Remove duplicates from country array based on id
 * @param {Array} countries - Array of country objects
 * @returns {Array} - Array with duplicates removed
 */
export const removeDuplicateCountries = (countries) => {
    if (!Array.isArray(countries)) return [];

    const seen = new Set();
    return countries.filter(country => {
        if (!country || typeof country.id === 'undefined') return false;

        if (seen.has(country.id)) {
            return false;
        }
        seen.add(country.id);
        return true;
    });
};
